<script lang="ts">
    import { onDestroy, onMount, setContext } from 'svelte'
    import { writable } from 'svelte/store'

    // make sure the stream is initialized
    import 'wdom/services/stream'

    import { Router } from '@mateothegreat/svelte5-router'

    import { gettext as _ } from 'hydria/services/gettext'
    import { alignToServer, DateTime, serverTimeDiffMs } from 'hydria/utils/time'

    import { createPortal } from 'wdom/services/simplePortal'

    import { routes, statuses } from './routes'

    import Notifications from 'hydria/components/Notifications.svelte'
    import Help from 'wdom/components/Help.svelte'
    import SessionCondition from 'wdom/components/SessionCondition.svelte'
    import Sidebar from './sidebar/Sidebar.svelte'
    import Topbar from './Topbar.svelte'

    // Minimum pixels to consider a touch event a "swipe" left or right
    const MIN_X_TRAVEL_FOR_SWIPE = 150
    // Maximum vertical pixels to consider a move as a horiz swipe
    const MAX_Y_TRAVEL_FOR_HORIZ_SWIPE = 50
    // Maximal milliseconds to consider an even a "swipe"
    const MAX_MS_FOR_SWIPE = 200

    let ready = $state(false)
    const appElement = document.getElementById('app')

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    setContext('signed-in-context', writable('signed-in'))

    onMount(() => {
        if (appElement) {
            appElement.classList.remove('loading-site')
            ready = true
        }
    })

    // Set to year, then adjust to server's year every time we get an update
    let serverYear = $state(DateTime.now().year)
    unsubs.push(
        serverTimeDiffMs.subscribe(() => {
            serverYear = alignToServer(DateTime.now()).year
        }),
    )

    let innerWidth: number | undefined = $state()
    let menuDefaultToOpen: boolean | undefined = $derived(innerWidth == null ? undefined : innerWidth >= 1150)

    // The left menu based on the user action.
    // Default means auto open/close based on window size.
    // Else we keep either open or closed, if the user changed the state.
    let userState: 'default' | 'open' | 'closed' = $state('default')

    // @ts-expect-error
    let menuIsOpen: boolean | undefined = $derived(userState == 'open' || (userState == 'default' && menuDefaultToOpen))

    function toggleState() {
        userState = menuIsOpen != menuDefaultToOpen ? 'default' : menuIsOpen ? 'closed' : 'open'
    }

    let touchData: any = {
        stamp: null,
        x: null,
        y: null,
    }
    function touchStart(e: TouchEvent) {
        if (['INPUT', 'A'].includes((e.target as Element).nodeName)) return
        touchData.stamp = performance.now()
        touchData.x = e.changedTouches[0].pageX
        touchData.y = e.changedTouches[0].pageY
    }
    function touchEnd(e: TouchEvent) {
        if (touchData.stamp == null) return
        const now = performance.now()
        const deltaX = e.changedTouches[0].pageX - touchData.x
        const deltaY = Math.abs(touchData.y - e.changedTouches[0].pageY)
        // console.log('∂; ms=', now - touchData.stamp, 'x=', deltaX, 'y:', deltaY)
        if (now - touchData.stamp < MAX_MS_FOR_SWIPE && deltaY < MAX_Y_TRAVEL_FOR_HORIZ_SWIPE && Math.abs(deltaX) > MIN_X_TRAVEL_FOR_SWIPE) {
            e.preventDefault()

            if ((deltaX < 0 && menuIsOpen) || (deltaX > 0 && !menuIsOpen)) {
                toggleState()
            }
            // Else nop.. the menu is already in the gesture's state
        }
        touchData.stamp = null
        touchData.x = null
        touchData.y = null
    }
</script>

<svelte:window bind:innerWidth ontouchstart={touchStart} ontouchend={touchEnd} />

<div id="body-wrap" class:menu-open={menuIsOpen} class:menu-closed={!menuIsOpen}>
    {#if ready}
        <aside id="print-header">WebDomaine.com</aside>
        <Topbar menuToggle={() => toggleState()} />
        <Sidebar />
        <div id="content-wrap">
            <main id="content"><Router {routes} {statuses} /></main>
            <footer id="footer" class="d-print-none">{$_('© {year} A.R.C. Informatique inc.', { year: serverYear })}</footer>
        </div>
    {/if}
</div>
<Notifications />
<Help />
<div id="minikeyboard-container" use:createPortal={'minikeyboard'}></div>
<SessionCondition />
